import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LoginInput, MeUser, RegisterInput } from '@ember/shared';
import { apiAuthMe, apiLogin, apiLogout, apiRegister } from '@/lib/api';
import { ApiError, isApiError, onUnauthenticated } from '@/lib/apiError';

interface AuthContextValue {
  user: MeUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  login: (input: LoginInput) => Promise<MeUser>;
  logout: () => Promise<void>;
  register: (input: RegisterInput) => Promise<{ userId: string }>;
  refetch: () => Promise<void>;
  setUser: (user: MeUser | null) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<MeUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const qc = useQueryClient();

  const hydrate = useCallback(async () => {
    try {
      const res = await apiAuthMe();
      setUser(res.user);
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        setUser(null);
      } else if (!(err instanceof ApiError)) {
        // network error — keep prior user state if any
      } else {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    return onUnauthenticated(() => {
      setUser(null);
      qc.clear();
    });
  }, [qc]);

  const login = useCallback(async (input: LoginInput) => {
    const res = await apiLogin(input);
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      /* swallow */
    }
    setUser(null);
    qc.clear();
  }, [qc]);

  const register = useCallback((input: RegisterInput) => apiRegister(input), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isInitialized,
      login,
      logout,
      register,
      refetch: hydrate,
      setUser,
    }),
    [user, isLoading, isInitialized, login, logout, register, hydrate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useUser(): MeUser {
  const { user } = useAuth();
  if (!user) throw new Error('useUser called without authenticated user');
  return user;
}
